import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Input,
  InputLabel,
  IconButton,
  FormHelperText,
  Paper,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import { API_ENDPOINTS } from "../../../config/api";

const API_URL = API_ENDPOINTS.BRANDS;

const EditBrandForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [brand, setBrand] = useState({
    name: "",
    logo: null,
    publish: false,
  });
  const [loading, setLoading] = useState(true);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });


  useEffect(() => {
    fetchBrand();
  }, []);

  const fetchBrand = async () => {
    try {
      const response = await axios.get(API_URL.EDIT(id));
      const { name, publish, logo } = response.data.brand;
      setBrand({
        name: name || "",
        publish: !!publish,
        logo: null,
      });
      setCurrentLogoUrl(logo || null);
      if (logo) setPreview(logo);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching brand:", error);
      setSnackbar({
        open: true,
        message: "Failed to load brand data",
        severity: "error",
      });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setBrand((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        setBrand((prev) => ({ ...prev, logo: null }));
        setPreview(null);
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        setBrand((prev) => ({ ...prev, logo: null }));
        setPreview(null);
        return;
      }
      setBrand((prev) => ({ ...prev, logo: file }));
      setError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setBrand((prev) => ({ ...prev, logo: null }));
    setPreview(null);
    setError("");
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
    if (snackbar.severity === "success") {
      navigate(-1);
    }
  };

  const handleUpdate = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", brand.name);
      formData.append("publish", brand.publish ? "1" : "0");
      if (brand.logo) {
        formData.append("logo", brand.logo);
      } else if (currentLogoUrl) {
        formData.append("logo", currentLogoUrl);
      }
      formData.append("_method", "PUT");

      await axios.post(API_URL.EDIT(id), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnackbar({
        open: true,
        message: "Brand updated successfully!",
        severity: "success",
      });
    } catch (error) {
      let message = error.response?.data?.message || "Something went wrong";
      if (message.includes("foreign key constraint")) {
        message = "Cannot update brand due to related data constraints. Please check your input.";
      }
      setSnackbar({
        open: true,
        message: `Failed to update brand: ${message}`,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />;
  }

  return (
    <Card sx={{ p: 4,  mx: "auto", mt: 5 }}>
      <Typography variant="h5" mb={3}>
        Edit Brand
      </Typography>
      <Box component="form" display="flex" flexDirection="column" gap={3}>
        <Grid container spacing={2}>
          
          
          <Grid item xs={12}>
              <FormControl fullWidth>
                <div className="image-field">
                  <Input
                    id="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    key={fileInputKey}
                    sx={{ display: 'none' }}
                  />
                  <label htmlFor="logo">
                    <Box
                      sx={{
                        border: '2px dashed #B0C4DE',
                        borderRadius: '10px',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#F0F8FF',
                        cursor: 'pointer',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                      }}
                    >
                      {preview ? (
                        <>
                          <img
                            src={brand.logo instanceof File ? preview : currentLogoUrl}
                            alt="Uploaded"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '80px',
                              marginBottom: '10px',
                              objectFit: 'contain',
                            }}
                          />
                          <Typography variant="body1" sx={{ color: '#4682B4', fontWeight: 'bold' }}>
                            {brand.logo instanceof File ? brand.name : "Current Logo"}
                          </Typography>
                          <IconButton
                            onClick={(e) => {
                              e.preventDefault();
                              handleRemoveFile();
                            }}
                            sx={{
                              position: 'absolute',
                              top: '5px',
                              right: '5px',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <UploadIcon
                            sx={{ fontSize: '40px', color: '#4682B4', marginBottom: '10px' }}
                          />
                          <Typography variant="body1" sx={{ color: '#4682B4', fontWeight: 'bold' }}>
                            Upload Brand Logo
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#4682B4' }}>
                            Supports: JPG, JPEG, PNG
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </div>
                {error && <FormHelperText error>{error}</FormHelperText>}
                <FormHelperText className="text-center">Upload brand logo (PNG, JPG, JPEG, max 5MB)</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                label="Brand Name"
                name="name"
                value={brand.name}
                onChange={handleChange}
                required
                error={!brand.name}
                helperText={!brand.name ? "Brand name is required" : ""}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="publish"
                  checked={brand.publish}
                  onChange={handleChange}
                />
              }
              label="Publish"
            />
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="space-between" gap={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={submitting || !brand.name}
          >
            {submitting ? "Updating..." : "Update"}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Cancel
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default EditBrandForm;